import React, { useState, useRef, useEffect } from 'react'
import { SubscriberForm } from '../SubscriberForm/SubscriberForm'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'

type UserType = 'individual' | 'center' | null

export const JoinForm: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<UserType>(null)
  const [shouldFocus, setShouldFocus] = useState(false)

  const handleBack = () => {
    setSelectedOption(null)
    setShouldFocus(true)
  }

  const individualButtonRef = useRef<HTMLButtonElement>(null)
  const centerButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (shouldFocus) {
      if (selectedOption === null) {
        // Focus the previously selected button
        if (individualButtonRef.current) {
          individualButtonRef.current.focus()
        } else if (centerButtonRef.current) {
          centerButtonRef.current.focus()
        }
      }
      setShouldFocus(false)
    }
  }, [selectedOption, shouldFocus])

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {selectedOption ? (
        <div>
          <div className="mb-8">
            <button
              onClick={handleBack}
              className="text-[#A6410A] hover:text-[#894d35] font-medium flex items-center"
            >
              <ChevronLeftIcon className="h-5 w-5 mr-2" />
              Back to selection
            </button>
          </div>
          <h2 className="text-3xl font-bold text-[#3C2416] mb-4">
            {selectedOption === 'individual'
              ? 'Individual Membership'
              : 'Center Membership'}
          </h2>
          <div className="mb-8 p-4 rounded-lg border border-[#9298a8]/20 bg-white shadow-md">
            <p className="text-[#3C2416] text-base leading-relaxed">
              {selectedOption === 'individual'
                ? 'By joining as an individual, you will be added to our email newsletter list and kept up to date with the latest research and happenings in Sickle Cell Disease research. Stay informed about breakthroughs, treatment advances, and NASCC initiatives.'
                : "After submitting this form, you will receive an email with a detailed application that needs to be filled out and submitted for review by our membership committee. This application will help us understand your center's capabilities and services."}
            </p>
          </div>
          <SubscriberForm
            centerApplicationRequest={selectedOption === 'center'}
          />
        </div>
      ) : (
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[#3C2416] mb-8">Join NASCC</h2>
          <p className="text-lg text-[#142841] mb-8">
            Please select how you would like to join:
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <div className="flex flex-col items-center max-w-sm">
              <button
                ref={individualButtonRef}
                onClick={() => setSelectedOption('individual')}
                className="w-full px-6 py-4 bg-[#A6410A] text-white rounded-lg hover:bg-[#894d35] transition-colors duration-200 text-lg font-medium mb-3"
              >
                Join as an Individual
              </button>
              <p className="text-sm text-[#142841] text-center leading-relaxed">
                Subscribe to our newsletter for the latest research, treatment
                advances, and NASCC updates
              </p>
            </div>
            <div className="flex flex-col items-center max-w-sm">
              <button
                ref={centerButtonRef}
                onClick={() => setSelectedOption('center')}
                className="w-full px-6 py-4 bg-[#142841] text-white rounded-lg hover:bg-[#9298a8] transition-colors duration-200 text-lg font-medium mb-3"
              >
                Join as a Sickle Cell Center
              </button>
              <p className="text-sm text-[#142841] text-center leading-relaxed">
                Request a membership application to become an official NASCC
                member center
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
